import { Link } from "react-router-dom";
import { ScanSearch } from "lucide-react";

const UPDATED = "June 2026";

const DOCS = {
  privacy: {
    title: "Privacy Policy",
    intro: "This Privacy Policy explains what information OpportunityScanner (\"we\", \"us\") collects when you use our business-analysis service, how we use it, and the choices you have.",
    sections: [
      ["Information we collect", [
        "Account information you provide: your name, email address, and a securely hashed password.",
        "Business information you enter for a scan: business name, location, website, category, and any reviews or competitors you choose to add.",
        "Payment information: payments are processed by our payment provider (Stripe). We never see or store your full card number. We store only a payment reference, amount, currency, and status.",
        "Proposal engagement: when a proposal is opened via a client link, we record that the link was accessed along with server-generated timestamps and a best-effort, privacy-preserving classification of whether the access looks like a person or an automated scanner. We do not store readable IP addresses, use invasive device fingerprinting, or track you across other websites.",
        "Usage data: basic technical logs needed to operate and secure the service.",
      ]],
      ["How we use information", [
        "To run business scans and generate reports, solutions, and proposals you request.",
        "To create and manage your account, proposals, and orders.",
        "To give the business that sent you a proposal basic, aggregated engagement insight (for example, whether a proposal link was opened) and to track its sales status.",
        "To process payments and provide receipts and support.",
        "To secure the service, prevent abuse, and comply with legal obligations.",
      ]],
      ["Public data & AI", [
        "Scans analyze publicly available information about a business's online presence.",
        "Reports are generated with the assistance of AI models over the data we collect. We label estimates and modeled figures clearly and do not fabricate reviews, revenue, or competitor statistics.",
      ]],
      ["Sharing", [
        "We do not sell your personal information.",
        "We share data only with service providers that help us operate (for example, payment processing and hosting), and when required by law.",
        "Proposals you choose to share via a client link are visible to anyone with that link, by design.",
      ]],
      ["Data retention & your choices", [
        "You may request deletion of your account and associated data by contacting us.",
        "You can delete proposals from your account at any time.",
      ]],
      ["Contact", [
        "Questions about this policy? Email support@opportunityscanner.com.",
      ]],
    ],
  },
  terms: {
    title: "Terms of Service",
    intro: "These Terms govern your use of OpportunityScanner. By creating an account or using the service, you agree to these Terms.",
    sections: [
      ["The service", [
        "OpportunityScanner analyzes a business's public online presence and produces reports, costed solutions, and proposals.",
        "Reports and figures are AI-assisted assessments and estimates. They are not financial, legal, tax, or professional advice and do not guarantee any result.",
      ]],
      ["Your account", [
        "You are responsible for maintaining the confidentiality of your login credentials and for all activity under your account.",
        "You must provide accurate information and use the service lawfully.",
      ]],
      ["Proposals & pricing", [
        "Prices shown are estimates generated from configurable pricing rules. Final scope and price are agreed between you and your service provider.",
        "Modeled opportunity figures are estimates of potential upside, not guaranteed revenue, savings, or ROI.",
      ]],
      ["Payments", [
        "Payments are processed securely by Stripe. By paying, you also agree to Stripe's terms.",
        "Amounts are charged in the currency and amount shown at checkout.",
      ]],
      ["Acceptable use", [
        "Do not attempt to disrupt the service, reverse engineer it, scrape it at scale, or use it to violate anyone's rights or applicable law.",
      ]],
      ["Disclaimers & liability", [
        "The service is provided \"as is\" without warranties of any kind.",
        "To the maximum extent permitted by law, we are not liable for indirect or consequential damages arising from your use of the service.",
      ]],
      ["Changes & contact", [
        "We may update these Terms; continued use means you accept the updated Terms.",
        "Questions? Email support@opportunityscanner.com.",
      ]],
    ],
  },
  refund: {
    title: "Refund & Cancellation Policy",
    intro: "This policy explains cancellations and refunds for payments made through OpportunityScanner.",
    sections: [
      ["Deposits & payments", [
        "Some engagements are started with a deposit (for example, 50%) and the balance billed later. Deposits secure your place in our delivery schedule.",
      ]],
      ["Cancellations", [
        "You may cancel before work begins for a full refund of any deposit paid.",
        "Once work has begun, refunds are prorated for work not yet delivered.",
      ]],
      ["Requesting a refund", [
        "To request a cancellation or refund, email support@opportunityscanner.com with your order or proposal reference.",
        "Approved refunds are returned to your original payment method, typically within 5–10 business days.",
      ]],
      ["Non-refundable items", [
        "Completed and delivered work is generally non-refundable.",
        "Third-party costs already incurred on your behalf may be non-refundable.",
      ]],
    ],
  },
  "ai-disclaimer": {
    title: "AI Disclaimer",
    intro: "OpportunityScanner uses artificial intelligence to help analyze public business data and draft reports, solutions, and proposals. Please read how to interpret these outputs.",
    sections: [
      ["What AI does here", [
        "AI helps summarize findings, draft recommended solutions, and estimate the scope of work.",
        "All pricing math is deterministic and rule-based — AI does not perform the financial calculations.",
      ]],
      ["What we never do", [
        "We do not fabricate reviews, revenue, competitor statistics, or dollar amounts.",
        "When information cannot be verified from a public source, we say so honestly rather than guessing.",
      ]],
      ["How to read the numbers", [
        "Figures labeled \"modeled\", \"estimated\", or \"AI-assisted estimate\" are projections based on assumptions — not guarantees.",
        "\"Modeled opportunity\" describes potential upside from addressing issues; it is not a promise of revenue, savings, or ROI.",
      ]],
      ["Your judgment matters", [
        "AI-generated assessments can contain errors. Use them as a starting point and verify important decisions with qualified professionals.",
      ]],
    ],
  },
};

export default function Legal({ doc }) {
  const d = DOCS[doc] || DOCS.privacy;
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 py-12" data-testid={`legal-${doc}`}>
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <span className="grid place-items-center w-9 h-9 rounded-lg bg-slate-900 text-white"><ScanSearch className="w-5 h-5" /></span>
          <span className="font-heading font-bold text-lg text-slate-900">Opportunity<span className="text-indigo-600">Scanner</span></span>
        </Link>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 sm:p-10">
          <h1 className="font-heading text-3xl font-bold text-slate-900">{d.title}</h1>
          <p className="text-sm text-slate-400 mt-1">Last updated: {UPDATED}</p>
          <p className="mt-5 text-slate-600 leading-relaxed">{d.intro}</p>
          <div className="mt-8 space-y-7">
            {d.sections.map(([heading, points], i) => (
              <section key={i}>
                <h2 className="font-heading text-lg font-semibold text-slate-900">{heading}</h2>
                <ul className="mt-2 space-y-2">
                  {points.map((pt, j) => (
                    <li key={j} className="text-sm text-slate-600 leading-relaxed list-disc list-inside">{pt}</li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
          <p className="mt-10 pt-6 border-t border-slate-100 text-xs text-slate-400 leading-relaxed">
            This document is a general template provided for convenience and is not legal advice. Consult a qualified attorney to adapt it for your jurisdiction and business.
          </p>
        </div>
      </div>
    </div>
  );
}
